// Turning server timestamps into something readable, and nothing else.
//
// The client formats time. It never computes one: every `createdAt` here came from a
// server response, and there is no `new Date()` in this package outside of this file's
// "is that today" comparison, which reads the clock and writes nothing.
//
// Absolute, not relative. "3 minutes ago" is a number that keeps changing on a screen
// somebody is reading slowly, and in a conversation that may go quiet for a fortnight it
// turns waiting into a counter — which is the one thing the waiting design is against.

import type { TopicView } from './api/contract.js';

const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dayAndTimeFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
const day = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export function dayAndTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return '';
  }
  const now = new Date();
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  return sameDay ? time.format(at) : dayAndTimeFormat.format(at);
}

export function dayOnly(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : day.format(at);
}

/**
 * A calendar date (`2026-08-27`) with no instant in it, as a release note carries.
 *
 * Not `dayOnly`: `new Date('2026-08-27')` is midnight UTC, which every timezone west of it
 * formats as the day before — a note dated the day before it shipped reads as a mistake.
 * A time with no zone on the end is parsed as local, which is what a bare date means here.
 */
export function calendarDay(date: string): string {
  const at = new Date(`${date}T00:00:00`);
  return Number.isNaN(at.getTime()) ? '' : day.format(at);
}

/** "Sally, David and Aunt Mary". The sentence the composer needs and nothing more. */
export function nameList(names: readonly string[]): string {
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0] ?? '';
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/**
 * "Erica", "Seth & Erica", "Seth, Erica & 3 others" — the room switcher's own convention
 * (an ampersand, and a collapse to "& N others" past two names) rather than `nameList`'s
 * prose join. A sibling to `nameList` rather than a variant of it, per
 * `docs/archived/13-visual-refresh.md`: the two are shaped for different places (a sentence next
 * to the composer versus a compact hover label) and neither should grow a parameter that
 * makes it do the other's job.
 */
export function roomNameList(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  if (names.length === 2) {
    return `${names[0]} & ${names[1]}`;
  }
  return `${names[0]}, ${names[1]} & ${names.length - 2} others`;
}

/** "Erica" from "Erica Hitchings" — everything before the first space, or the whole string
 * if there isn't one. Not a real name parser (a hyphenated given name, a name in a script
 * with no spaces, "Mary Jane" as one given name all split wrong) — this app tracks no
 * separate first and last name at all, and reciting someone's full name every time they're
 * mentioned reads worse than the rare wrong split. */
export function firstName(name: string): string {
  const space = name.indexOf(' ');
  return space === -1 ? name : name.slice(0, space);
}

/** "Your wife" or "Seth's wife": the word as recorded, through the reader or through someone else. */
export function relationLabel(relation: {
  readonly word: string;
  readonly via: { readonly name: string } | null;
}): string {
  return relation.via === null
    ? `Your ${relation.word}`
    : `${firstName(relation.via.name)}'s ${relation.word}`;
}

/** The other people in the principals' room, by first name — "Maya", "Maya and Tom",
 * "Maya, Tom and Ann" — read off that thread's own participants. Falls back to a phrase that
 * names nobody when the thread or its participants are not available. */
export function principalsRoomLabel(
  thread: { readonly participants: readonly string[] } | null,
  people: readonly {
    readonly id: string;
    readonly name: string;
    readonly self: boolean;
  }[],
): string {
  const fallback = 'the other principals';
  if (thread === null) {
    return fallback;
  }
  const names = thread.participants
    .map((id) => people.find((person) => person.id === id))
    .filter((person) => person !== undefined && !person.self)
    .map((person) => firstName(person!.name));
  if (names.length === 0) {
    return fallback;
  }
  return names.length === 1
    ? names[0]!
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
}

/**
 * "Added by Maya · 1 Oct" when the item has never been changed since it was added (its created
 * and changed times are equal), "Changed by Maya · 1 Oct" otherwise. Null when the reader is
 * the one who added or changed it, or when nothing says who did. With no recorded person the
 * line is "Added · date" or "Changed · date". Names come from the people list the screen
 * already holds.
 */
export function changedLine(
  changedBy: string | null,
  changedAt: string | null,
  createdAt: string | null,
  people: readonly {
    readonly id: string;
    readonly name: string;
    readonly self: boolean;
  }[],
): string | null {
  const at = changedAt === null ? null : new Date(changedAt);
  const when =
    at === null || Number.isNaN(at.getTime())
      ? null
      : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(at);
  const verb = createdAt !== null && createdAt === changedAt ? 'Added' : 'Changed';
  if (changedBy === null || changedBy === '') {
    return when === null ? null : `${verb} · ${when}`;
  }
  const who = people.find((person) => person.id === changedBy);
  if (who === undefined || who.self) {
    return null;
  }
  const name = firstName(who.name);
  return when === null ? `${verb} by ${name}` : `${verb} by ${name} · ${when}`;
}

// The phrase a topic goes by on the home page and at the head of its own pages; the short
// name (`label`) stays on the bar across the top. The words come from the server.
export const topicPhrase = (topic: Pick<TopicView, 'phrase'>): string => topic.phrase;

/** The line under the title on a topic's list page. */
export function topicListDescription(topic: Pick<TopicView, 'description'>): string {
  return topic.description;
}
